import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import wLogo from '@/assets/want-w-icon.svg';
import { Skeleton } from '@/components/ui/skeleton';

const LOADING_MESSAGES = [
  'Analyzing your image…',
  'Identifying products…',
  'Searching stores…',
  'Finding the best matches…',
];

const PROGRESS_DURATION = 15; // seconds to fill bar

export function ProductDiscoveryLoading() {
  const [msgIndex, setMsgIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  // Cycle status messages every 3s
  useEffect(() => {
    const id = setInterval(() => {
      setMsgIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  // Animate progress from 0 → 95 over PROGRESS_DURATION
  useEffect(() => {
    const start = Date.now();
    const tick = () => {
      const elapsed = (Date.now() - start) / 1000;
      const pct = Math.min(95, (elapsed / PROGRESS_DURATION) * 95);
      setProgress(pct);
      if (pct < 95) raf = requestAnimationFrame(tick);
    };
    let raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="py-6 flex flex-col items-center gap-5"
    >
      {/* Logo pulse */}
      <div className="relative">
        <motion.div
          className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <motion.img
            src={wLogo}
            alt="Loading"
            className="w-7 h-7"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-primary/30"
          animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>

      {/* Status text */}
      <div className="text-center space-y-1 h-12">
        <AnimatePresence mode="wait">
          <motion.p
            key={msgIndex}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="font-semibold text-foreground"
          >
            {LOADING_MESSAGES[msgIndex]}
          </motion.p>
        </AnimatePresence>
        <p className="text-xs text-muted-foreground">This usually takes 10–15 seconds</p>
      </div>

      {/* Progress bar */}
      <div className="w-full max-w-xs h-1.5 rounded-full bg-muted overflow-hidden">
        <motion.div
          className="h-full rounded-full bg-primary"
          style={{ width: `${progress}%` }}
          transition={{ ease: 'linear' }}
        />
      </div>

      {/* Skeleton product cards */}
      <div className="w-full grid grid-cols-2 gap-3 mt-1">
        {[0, 1].map((i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="aspect-[3/4] w-full rounded-xl" />
            <Skeleton className="h-3.5 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ))}
      </div>
    </motion.div>
  );
}
